(() => {
  const ROOT_SELECTOR =
    '[data-lk-wholesale-catalog], [data-lk-paypal-cart]';

  const ORDER_KEY =
    'luckich-wholesale-order-v2';

  function readOrder() {
    try {
      const value = JSON.parse(
        localStorage.getItem(ORDER_KEY) || '[]'
      );

      return Array.isArray(value)
        ? value
        : [];
    } catch (_) {
      return [];
    }
  }

  function apiBase(root) {
    return String(
      root.dataset.paypalUrl || ''
    )
      .trim()
      .replace(/\/+$/, '');
  }

  function normalizeItems(items) {
    return items
      .filter(
        (item) =>
          item?.properties?.Wholesale === 'Yes'
      )
      .map((item) => ({
        styleCode: String(
          item.properties['Style Code'] ||
          item.sku ||
          ''
        )
          .trim()
          .toUpperCase(),

        size: String(
          item.properties.Size || ''
        )
          .trim()
          .toUpperCase(),

        shape: String(
          item.properties['Nail Shape'] || ''
        ).trim(),

        note: String(
          item.properties['Nail shape / style note'] || ''
        ).trim(),

        unitPriceCents: Number(
          item.final_price || item.price || 0
        ),

        quantity: Number(
          item.quantity || 0
        )
      }));
  }

  function normalizeTip(value) {
    const cents = Math.round(Number(value || 0) * 100);
    return [0, 500, 1000, 2000, 5000].includes(cents) ? cents : 0;
  }

  async function getOrderData(root) {
    if (root.matches('[data-lk-paypal-cart]')) {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Unable to read the Shopify cart.');
      }

      const cart = await response.json();
      return {
        items: normalizeItems(cart.items || []),
        tipCents: normalizeTip(cart.attributes?.['Wholesale Tip'])
      };
    }

    return { items: normalizeItems(readOrder()), tipCents: 0 };
  }

  function showToast(
    root,
    message,
    isError = false
  ) {
    const toast = root.querySelector(
      '[data-wholesale-toast]'
    );

    if (!toast) return;

    toast.textContent = message;

    toast.classList.toggle(
      'is-error',
      isError
    );

    toast.classList.add('is-visible');

    setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 5000);
  }

  async function beginPayPalPayment(
    root,
    button
  ) {
    const baseUrl = apiBase(root);
    let items = [];

    if (!baseUrl) {
      showToast(
        root,
        'Add the PayPal Worker URL in the Theme Editor first.',
        true
      );

      return;
    }

    const previousText =
      button.textContent;

    button.setAttribute(
      'aria-disabled',
      'true'
    );

    /* Legacy loading label was corrupted during an earlier theme transfer.
    button.textContent =
      'CREATING SECURE PAYPAL ORDER…';

    */
    button.textContent =
      'CREATING SECURE PAYPAL ORDER...';

    try {
      const order = await getOrderData(root);
      items = order.items;

      if (!items.length) {
        throw new Error('Add at least one wholesale style before payment.');
      }

      const response = await fetch(
        `${baseUrl}/paypal/create-order`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json'
          },

          body: JSON.stringify({
            items,
            itemSubtotalCents: items.reduce(
              (sum, item) => sum + item.unitPriceCents * item.quantity,
              0
            ),
            shippingCents: items.reduce(
              (sum, item) => sum + item.unitPriceCents * item.quantity,
              0
            ) >= 20000 ? 0 : 3000,
            tipCents: order.tipCents
          })
        }
      );

      const data = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
          'Unable to create the PayPal order.'
        );
      }

      if (!data.approveUrl) {
        throw new Error(
          'PayPal approval URL was not returned.'
        );
      }

      sessionStorage.setItem(
        'luckich-paypal-order-id',
        data.orderId || ''
      );

      window.location.assign(
        data.approveUrl
      );
    } catch (error) {
      button.removeAttribute(
        'aria-disabled'
      );

      button.textContent =
        previousText;

      showToast(
        root,
        error.message ||
        'Unable to start PayPal.',
        true
      );
    }
  }

  function handlePayPalReturn(root) {
    const url = new URL(
      window.location.href
    );

    const result =
      url.searchParams.get('paypal');

    if (!result) return;

    if (result === 'success') {
      localStorage.removeItem(ORDER_KEY);

      url.searchParams.delete('paypal');
      url.searchParams.delete(
        'paypal_order'
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );

      showToast(
        root,
        'PayPal payment completed. Thank you!'
      );

      setTimeout(() => {
        window.location.reload();
      }, 1200);

      return;
    }

    url.searchParams.delete('paypal');
    url.searchParams.delete(
      'paypal_order'
    );

    window.history.replaceState(
      {},
      '',
      url.toString()
    );

    showToast(
      root,
      result === 'cancelled'
        ? 'PayPal payment was cancelled.'
        : 'PayPal payment could not be completed.',
      true
    );
  }

  function keepButtonLabel(root) {
    const button = root.querySelector(
      '[data-wholesale-paypal]'
    );

    if (!button || !apiBase(root)) {
      return;
    }

    const label =
      button.dataset.paypalLabel || 'PAY ORDER WITH PAYPAL';

    const update = () => {
      if (
        !button.textContent.includes(
          'CREATING'
        ) &&
        button.textContent !== label
      ) {
        button.textContent = label;
      }
    };

    update();

    new MutationObserver(update).observe(
      button,
      {
        childList: true,
        characterData: true,
        subtree: true
      }
    );
  }

  function initialize(root) {
    root.addEventListener(
      'click',
      (event) => {
        const button =
          event.target.closest(
            '[data-wholesale-paypal]'
          );

        if (!button) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        beginPayPalPayment(
          root,
          button
        );
      },
      true
    );

    keepButtonLabel(root);
    handlePayPalReturn(root);
  }

  document
    .querySelectorAll(ROOT_SELECTOR)
    .forEach(initialize);
})();
